import React, { useState, useCallback, useEffect } from 'react';
import { useMap } from '../map/MapContext';
import {
  applyMapSettings,
  loadMapSettings,
  saveMapSettings,
  type AppSettings,
} from '../services/map-settings';
import {
  clearStore,
  evictAllExpired,
  getStats,
  type StoreName,
} from '../services/CacheService';

type CacheStats = Awaited<ReturnType<typeof getStats>>;
const EMPTY_CACHE_STATS: CacheStats = { charts: 0, metadata: 0, tiles: 0, userData: 0 };

export const SettingsPanel: React.FC = () => {
  const { map } = useMap();
  const [settings, setSettings] = useState<AppSettings>(loadMapSettings);
  const [cacheStats, setCacheStats] = useState<CacheStats>(EMPTY_CACHE_STATS);
  const [cacheBusy, setCacheBusy] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<string | null>(null);

  const update = useCallback(<K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      saveMapSettings(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (map) applyMapSettings(map, settings);
  }, [map, settings]);

  const refreshCacheStats = useCallback(async () => {
    try {
      setCacheStats(await getStats());
    } catch {
      setCacheStatus('Offline cache is unavailable in this browser.');
    }
  }, []);

  useEffect(() => {
    void refreshCacheStats();
  }, [refreshCacheStats]);

  const clearCache = useCallback(async (storeName: StoreName) => {
    setCacheBusy(true);
    setCacheStatus(null);
    try {
      await clearStore(storeName);
      await refreshCacheStats();
      setCacheStatus(`${storeName[0].toUpperCase()}${storeName.slice(1)} cache cleared.`);
    } catch {
      setCacheStatus('Offline cache could not be cleared.');
    } finally {
      setCacheBusy(false);
    }
  }, [refreshCacheStats]);

  const removeExpired = useCallback(async () => {
    setCacheBusy(true);
    setCacheStatus(null);
    try {
      const removed = await evictAllExpired();
      await refreshCacheStats();
      setCacheStatus(`Removed ${removed.tiles + removed.charts + removed.metadata} expired cache entries.`);
    } catch {
      setCacheStatus('Expired cache cleanup failed.');
    } finally {
      setCacheBusy(false);
    }
  }, [refreshCacheStats]);

  return (
    <div>
      <div className="panel-section">
        <div className="panel-section-title">Coordinate Format</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['DD', 'DMS', 'DDM', 'UTM', 'MGRS'] as const).map((f) => (
            <button key={f} className={`chip ${settings.coordinateFormat === f ? 'active' : ''}`} onClick={() => update('coordinateFormat', f)}>{f}</button>
          ))}
        </div>
      </div>

      <div className="panel-section">
        <div className="panel-section-title">Distance Units</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {([['nautical', 'NM'], ['statute', 'SM'], ['metric', 'km']] as const).map(([val, label]) => (
            <button key={val} className={`chip ${settings.units === val ? 'active' : ''}`} onClick={() => update('units', val)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="panel-section">
        <div className="panel-section-title">Map Options</div>
        <label className="toggle" style={{ display: 'flex', padding: '6px 0' }}>
          <span className={`toggle-box ${settings.mapBearing ? 'checked' : ''}`} onClick={() => update('mapBearing', !settings.mapBearing)} />
          <span>Allow map rotation</span>
        </label>
        <label className="toggle" style={{ display: 'flex', padding: '6px 0' }}>
          <span className={`toggle-box ${settings.mapTerrain3d ? 'checked' : ''}`} onClick={() => update('mapTerrain3d', !settings.mapTerrain3d)} />
          <span>3D Terrain</span>
        </label>
        {settings.mapTerrain3d && (
          <div style={{ paddingLeft: 26 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Exaggeration: {settings.terrainExaggeration.toFixed(1)}×</div>
            <input
              type="range" min="0.5" max="5" step="0.5"
              value={settings.terrainExaggeration}
              onChange={(e) => update('terrainExaggeration', parseFloat(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>

      <div className="panel-section">
        <div className="panel-section-title">Offline Cache</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, fontSize: 12, marginBottom: 8 }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Tiles</span><br />{cacheStats.tiles.toLocaleString()}</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Charts</span><br />{cacheStats.charts.toLocaleString()}</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Metadata</span><br />{cacheStats.metadata.toLocaleString()}</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Saved</span><br />{cacheStats.userData.toLocaleString()}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" disabled={cacheBusy} onClick={() => void clearCache('tiles')}>Clear tiles</button>
          <button className="btn btn-secondary" disabled={cacheBusy} onClick={() => void clearCache('charts')}>Clear charts</button>
          <button className="btn btn-secondary" disabled={cacheBusy} onClick={() => void clearCache('metadata')}>Clear metadata</button>
          <button className="btn btn-secondary" disabled={cacheBusy} onClick={() => void removeExpired()}>Remove expired</button>
        </div>
        {cacheStatus && (
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 6 }}>{cacheStatus}</div>
        )}
      </div>

      <div className="panel-section">
        <div className="panel-section-title">API Keys</div>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>OpenWeatherMap API Key</label>
        <input
          className="input"
          placeholder="Paste OWM API key for weather tiles"
          value={settings.owmApiKey}
          onChange={(e) => update('owmApiKey', e.target.value)}
          type="password"
        />
      </div>

      <div className="panel-section">
        <div className="panel-section-title">About</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <p>Aviation EFB – Web PWA</p>
          <p>Built with MapLibre GL JS + React</p>
          <p>Data via OGC API Features / Tiles</p>
        </div>
      </div>
    </div>
  );
};
